/**
 * React Query hook for Case and Lead List (used by Timer Overlay)
 * Provides automatic caching to prevent redundant API calls on navigation
 * Supports keyword search for filtering cases
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState, useMemo } from 'react';
import debounce from 'lodash.debounce';
import { listCasesAndLeads } from 'services/CaseServices';
import { queryKeys } from '../config/queryClient';

/**
 * Fetches case and lead list for timer selection
 *
 * @param {string} keyword - Search keyword for filtering
 * @returns {Promise<Array>} - Combined array of cases and leads
 */
const fetchCaseLeadList = async (keyword = '') => {
  try {
    const response = await listCasesAndLeads(false, null, false, keyword, 20, true, [], true);
    const cases = response?.data?.case_list || [];
    const leads = response?.data?.lead_list || [];
    return [...cases, ...leads];
  } catch (error) {
    console.error('Error loading cases:', error);
    throw error;
  }
};

/**
 * Hook to fetch case and lead list with React Query for timer overlay
 * Caches results to prevent redundant API calls on page navigation
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 *
 * @returns {Object} - casesList, isLoading, error, refetch, searchKeyword, handleSearch, resetSearch
 */
export const useCaseLeadListQuery = ({ enabled = true } = {}) => {
  const queryClient = useQueryClient();
  const [searchKeyword, setSearchKeyword] = useState('');

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: queryKeys.timerCaseLeadList.list(searchKeyword),
    queryFn: () => fetchCaseLeadList(searchKeyword),
    enabled,
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    keepPreviousData: true, // Keep previous data while fetching new search results
    retry: 2, // Retry twice on failure
  });

  /**
   * Debounced search handler to avoid firing requests on every keystroke
   */
  const debouncedSearch = useMemo(
    () =>
      debounce((keyword) => {
        setSearchKeyword(keyword);
      }, 400),
    []
  );

  /**
   * Handle search input change
   * @param {string} keyword - Search keyword
   */
  const handleSearch = useCallback(
    (keyword) => {
      debouncedSearch(keyword);
    },
    [debouncedSearch]
  );

  /**
   * Reset search keyword and cancel any pending debounced searches
   */
  const resetSearch = useCallback(() => {
    debouncedSearch.cancel();
    setSearchKeyword('');
  }, [debouncedSearch]);

  /**
   * Invalidate the case-lead list cache
   */
  const invalidateCaseLeadList = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.timerCaseLeadList.all });
  }, [queryClient]);

  return {
    casesList: data || [],
    isLoading,
    isFetching,
    error,
    refetch,
    searchKeyword,
    handleSearch,
    resetSearch,
    invalidateCaseLeadList,
  };
};
